import React from 'react';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'text',
  width,
  height,
  animation = 'pulse',
}) => {
  const getVariantClass = () => {
    switch (variant) {
      case 'circular':
        return 'rounded-full';
      case 'rectangular':
        return 'rounded-lg';
      case 'text':
      default:
        return 'rounded';
    }
  };

  const getAnimationClass = () => {
    switch (animation) {
      case 'wave':
        return 'animate-shimmer';
      case 'none':
        return '';
      case 'pulse':
      default:
        return 'animate-pulse';
    }
  };

  const style: React.CSSProperties = {
    width: width,
    height: height || (variant === 'text' ? '1em' : undefined),
  };

  return (
    <div
      className={`bg-gray-200 ${getVariantClass()} ${getAnimationClass()} ${className}`}
      style={style}
    />
  );
};

// Preset skeleton components for common use cases
export const SkeletonText: React.FC<{ lines?: number; className?: string }> = ({
  lines = 1,
  className = '',
}) => (
  <div className={`space-y-2 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        variant="text"
        width={i === lines - 1 && lines > 1 ? '75%' : '100%'}
        height="1rem"
      />
    ))}
  </div>
);

export const SkeletonAvatar: React.FC<{ size?: number; className?: string }> = ({
  size = 40,
  className = '',
}) => (
  <Skeleton
    variant="circular"
    width={size}
    height={size}
    className={className}
  />
);

export const SkeletonButton: React.FC<{ width?: number; className?: string }> = ({
  width = 100,
  className = '',
}) => (
  <Skeleton
    variant="rectangular"
    width={width}
    height={36}
    className={className}
  />
);

// Table skeleton
export const SkeletonTable: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 5,
}) => (
  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
    {/* Header */}
    <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
      <div className="flex space-x-4">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} variant="text" width="100px" height="12px" />
        ))}
      </div>
    </div>
    {/* Body */}
    <div className="divide-y divide-gray-200">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="px-6 py-4">
          <div className="flex space-x-4 items-center">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton
                key={colIndex}
                variant="text"
                width={colIndex === 0 ? '150px' : '100px'}
                height="16px"
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Stats card skeleton
export const SkeletonStatsCard: React.FC = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-6">
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <Skeleton variant="text" width="80px" height="14px" />
        <Skeleton variant="text" width="60px" height="28px" />
      </div>
      <Skeleton variant="circular" width={48} height={48} />
    </div>
  </div>
);

// Dashboard skeleton
export const SkeletonDashboard: React.FC = () => (
  <div className="space-y-6">
    {/* Stats */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {Array.from({ length: 4 }).map((_, i) => (
        <SkeletonStatsCard key={i} />
      ))}
    </div>
    {/* Table */}
    <SkeletonTable rows={5} columns={5} />
  </div>
);

// Card skeleton
export const SkeletonCard: React.FC<{ hasImage?: boolean }> = ({ hasImage = false }) => (
  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
    {hasImage && <Skeleton variant="rectangular" width="100%" height={200} />}
    <div className="p-4 space-y-3">
      <Skeleton variant="text" width="70%" height="20px" />
      <SkeletonText lines={2} />
      <div className="flex space-x-2 pt-2">
        <SkeletonButton width={80} />
        <SkeletonButton width={80} />
      </div>
    </div>
  </div>
);

// List skeleton
export const SkeletonList: React.FC<{ items?: number }> = ({ items = 5 }) => (
  <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200">
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="p-4 flex items-center space-x-4">
        <SkeletonAvatar size={40} />
        <div className="flex-1 space-y-2">
          <Skeleton variant="text" width="40%" height="16px" />
          <Skeleton variant="text" width="60%" height="14px" />
        </div>
        <Skeleton variant="rectangular" width={24} height={24} />
      </div>
    ))}
  </div>
);

export default Skeleton;
